import { useState } from 'react';
import { T, Var } from 'gt-react';
import { format, parseISO } from 'date-fns';
import {
  ChatTextIcon,
  CheckCircleIcon,
  UserIcon,
  GearIcon,
  ArrowsClockwiseIcon,
  NoteIcon,
  NotePencilIcon,
  EnvelopeSimpleIcon,
  PaperclipIcon,
  FileTextIcon,
} from '@phosphor-icons/react';
import type { CollectionThreadActivityDto, InvoiceEvent } from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/components/common/Currency';
import { InboundEmailDetailSheet } from './InboundEmailDetailSheet';
import { OutboundEmailDetailSheet } from '@/components/communications/OutboundEmailDetailSheet';
import { DocumentDetailSheet } from './DocumentDetailSheet';
import { RESOLUTION_LABELS, isValidResolution } from './threadLabels';
import { formatFollowUpTime } from '@/components/common/waiting-on/followUpFormat';
import {
  ActivityRail,
  ActivityWhen,
  type ActivityTone,
} from '@/components/common/activity-timeline/ActivityTimelinePrimitives';

interface Props {
  activities: CollectionThreadActivityDto[];
  invoiceEvents?: InvoiceEvent[];
}

// Invoice lifecycle events worth surfacing on the thread timeline. Famous
// transaction notes/comments (`note_added`) are deliberately excluded — high
// volume, and already visible on the invoice detail page's own Activity tab.
const MERGED_INVOICE_EVENT_TYPES = new Set([
  'invoice_opened',
  'invoice_sent',
  'remittance_received',
  'payment_received',
]);

type TimelineEntry =
  | { source: 'thread'; time: number; activity: CollectionThreadActivityDto }
  | { source: 'invoice'; time: number; event: InvoiceEvent };

export function CollectionThreadActivityTimeline({
  activities,
  invoiceEvents = [],
}: Props) {
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);
  const [openOutboundEmailId, setOpenOutboundEmailId] = useState<string | null>(
    null,
  );
  const [openDocumentId, setOpenDocumentId] = useState<string | null>(null);

  const mergedInvoiceEvents = invoiceEvents.filter((ev) =>
    MERGED_INVOICE_EVENT_TYPES.has(ev.eventType),
  );

  if (activities.length === 0 && mergedInvoiceEvents.length === 0) {
    return (
      <p className="text-sm text-muted-foreground px-3 py-4">
        <T context="empty state, no activity yet">
          No activity yet — be the first to comment.
        </T>
      </p>
    );
  }

  // Newest first so the most recent event is at the top of the feed.
  const entries: TimelineEntry[] = [
    ...activities.map((activity): TimelineEntry => ({
      source: 'thread',
      time: new Date(activity.createdAt).getTime(),
      activity,
    })),
    ...mergedInvoiceEvents.map((event): TimelineEntry => ({
      source: 'invoice',
      time: new Date(event.occurredAt).getTime(),
      event,
    })),
  ].sort((a, b) => b.time - a.time);

  return (
    <>
      <ul className="px-3 py-2">
        {entries.map((entry, i) => {
          const isLast = i === entries.length - 1;
          return entry.source === 'thread' ? (
            <ActivityRow
              key={`activity-${entry.activity.id}`}
              activity={entry.activity}
              isLast={isLast}
              onViewEmail={setOpenEmailId}
              onViewOutboundEmail={setOpenOutboundEmailId}
              onViewDocument={setOpenDocumentId}
            />
          ) : (
            <InvoiceEventRow
              key={`invoice-event-${entry.event.id}`}
              event={entry.event}
              isLast={isLast}
            />
          );
        })}
      </ul>
      <InboundEmailDetailSheet
        inboundEmailId={openEmailId}
        onClose={() => setOpenEmailId(null)}
      />
      <OutboundEmailDetailSheet
        outboundEmailId={openOutboundEmailId}
        onClose={() => setOpenOutboundEmailId(null)}
      />
      <DocumentDetailSheet
        documentId={openDocumentId}
        onClose={() => setOpenDocumentId(null)}
      />
    </>
  );
}

interface RowProps {
  activity: CollectionThreadActivityDto;
  isLast: boolean;
  onViewEmail: (inboundEmailId: string) => void;
  onViewOutboundEmail: (outboundEmailId: string) => void;
  onViewDocument: (documentId: string) => void;
}

function ActivityRow({
  activity,
  isLast,
  onViewEmail,
  onViewOutboundEmail,
  onViewDocument,
}: RowProps) {
  const isComment = activity.kind === 'comment';

  if (isComment) {
    return (
      <ActivityRail
        tone="note"
        icon={<NoteIcon className="h-3.5 w-3.5" />}
        isLast={isLast}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {activity.actorName ? (
              <T context="activity feed, note added by named user">
                Note added by <Var>{activity.actorName}</Var>
              </T>
            ) : (
              <T context="activity feed, who added a note">Note added</T>
            )}
          </span>
          <ActivityWhen at={activity.createdAt} />
        </div>
        <div className="text-sm text-foreground mt-1 whitespace-pre-wrap rounded-md bg-amber-50 border-l-2 border-amber-300 px-3 py-1.5">
          {activity.body}
        </div>
      </ActivityRail>
    );
  }

  if (activity.kind === 'next_step') {
    // A rep's recorded next step: waiting-on reason + follow-up date in the
    // payload (label snapshotted at write time so a later rename doesn't
    // rewrite history), optional note in the body. First-class rather than
    // falling through to the generic system-event branch.
    const payload = activity.payload ?? {};
    const waitingOnLabel =
      typeof payload.waitingOnLabel === 'string' && payload.waitingOnLabel
        ? payload.waitingOnLabel
        : null;
    // Parse defensively: `format` throws on Invalid Date, which would take
    // down the whole timeline over one malformed payload value.
    const followUpDate =
      typeof payload.followUpAt === 'string' && payload.followUpAt
        ? parseISO(payload.followUpAt)
        : null;
    const followUpAt =
      followUpDate && !Number.isNaN(followUpDate.getTime())
        ? followUpDate
        : null;
    return (
      <ActivityRail
        tone="note"
        icon={<NotePencilIcon className="h-3.5 w-3.5" />}
        isLast={isLast}
      >
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {activity.actorName ? (
              <T context="activity feed, next step recorded by named user">
                Next step set by <Var>{activity.actorName}</Var>
              </T>
            ) : (
              <T context="activity feed, next step recorded">Next step set</T>
            )}
          </span>
          <ActivityWhen at={activity.createdAt} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {waitingOnLabel ? (
            <Badge variant="outline" className="text-xs">
              {waitingOnLabel}
            </Badge>
          ) : (
            <span>
              <T context="activity feed, the thread's parked state was cleared">
                Cleared — needs triage
              </T>
            </span>
          )}
          {followUpAt && (
            <span>
              <T context="activity feed, follow-up date recorded on the thread">
                Follow up{' '}
                <Var>
                  {(() => {
                    const time = formatFollowUpTime(followUpAt);
                    const day = format(followUpAt, 'MMM d, yyyy');
                    return time ? `${day}, ${time}` : day;
                  })()}
                </Var>
              </T>
            </span>
          )}
        </div>
        {activity.body && (
          <div className="text-sm text-foreground mt-1 whitespace-pre-wrap rounded-md bg-amber-50 border-l-2 border-amber-300 px-3 py-1.5">
            {activity.body}
          </div>
        )}
      </ActivityRail>
    );
  }

  const filedEmail = readFiledEmailPreview(activity);
  const sentEmail = readSentEmailPreview(activity);
  const filedDocument = readFiledDocumentPreview(activity);
  // Consts so TS narrows the nullable ids through the onView closures.
  const filedEmailId = filedEmail?.inboundEmailId ?? null;
  const sentEmailId = sentEmail?.outboundEmailId ?? null;

  return (
    <ActivityRail
      tone={toneFor(activity.kind)}
      icon={iconFor(activity.kind)}
      isLast={isLast}
    >
      <div className="flex items-baseline gap-2">
        <span className="text-sm text-foreground font-medium">
          {describe(activity)}
        </span>
        {activity.actorName && (
          <span className="text-xs text-muted-foreground">
            <T context="activity feed, action performed by named user">
              by <Var>{activity.actorName}</Var>
            </T>
          </span>
        )}
        <ActivityWhen at={activity.createdAt} />
      </div>
      {filedEmail && (
        <EmailActivityCard
          subject={filedEmail.subject}
          addressLine={
            filedEmail.from ? (
              <T context="email sender line">
                From <Var>{filedEmail.from}</Var>
              </T>
            ) : null
          }
          snippet={filedEmail.snippet}
          attachmentCount={filedEmail.attachmentCount}
          onView={filedEmailId ? () => onViewEmail(filedEmailId) : null}
        />
      )}
      {sentEmail && (
        <EmailActivityCard
          subject={sentEmail.subject}
          addressLine={
            sentEmail.to ? (
              <T context="email recipient line">
                To <Var>{sentEmail.to}</Var>
              </T>
            ) : null
          }
          snippet={sentEmail.snippet}
          attachmentCount={sentEmail.attachmentCount}
          onView={sentEmailId ? () => onViewOutboundEmail(sentEmailId) : null}
        />
      )}
      {filedDocument && (
        <FiledDocumentCard preview={filedDocument} onView={onViewDocument} />
      )}
    </ActivityRail>
  );
}

interface InvoiceEventRowProps {
  event: InvoiceEvent;
  isLast: boolean;
}

function invoiceEventToneFor(eventType: string): ActivityTone {
  if (eventType === 'payment_received') return 'amber';
  if (eventType === 'remittance_received') return 'note';
  return 'stone';
}

function invoiceEventIconFor(eventType: string): React.ReactNode {
  if (eventType === 'payment_received')
    return <CheckCircleIcon className="h-3.5 w-3.5" />;
  if (eventType === 'invoice_opened')
    return <FileTextIcon className="h-3.5 w-3.5" />;
  return <EnvelopeSimpleIcon className="h-3.5 w-3.5" />;
}

/**
 * `remittance_received` is the one event type sourced from LLM extraction of
 * a remittance document rather than confirmed ERP/bank data (see
 * `InvoiceEventType.remittanceReceived`) — flagged here so it never reads
 * identical to a confirmed `payment_received` event.
 */
function isUnconfirmedInvoiceEvent(event: InvoiceEvent): boolean {
  return event.payload?.confirmed === false;
}

function InvoiceEventRow({ event, isLast }: InvoiceEventRowProps) {
  const unconfirmed = isUnconfirmedInvoiceEvent(event);
  return (
    <ActivityRail
      tone={invoiceEventToneFor(event.eventType)}
      icon={invoiceEventIconFor(event.eventType)}
      isLast={isLast}
    >
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm text-foreground font-medium capitalize">
          {event.eventType.replace(/_/g, ' ')}
        </span>
        {event.amount && (
          <span className="text-sm text-foreground">
            {formatCurrency(event.amount)}
          </span>
        )}
        {unconfirmed && (
          <span className="inline-flex items-center rounded-full border border-dashed border-muted-foreground/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            <T context="badge, remittance-derived amount not yet confirmed by an applied payment">
              Customer-claimed, unconfirmed
            </T>
          </span>
        )}
        <ActivityWhen at={event.occurredAt} />
      </div>
      {event.note && (
        <div className="text-sm text-muted-foreground mt-1">{event.note}</div>
      )}
    </ActivityRail>
  );
}

interface FiledEmailPreview {
  inboundEmailId: string | null;
  subject: string | null;
  from: string | null;
  snippet: string | null;
  attachmentCount: number;
}

/**
 * Reads the point-in-time email preview off an email-evidence system event —
 * rep forwards (`evidence_filed_via_email`), content auto-matches
 * (`evidence_auto_matched`), and uploaded email files
 * (`evidence_filed_via_upload`) all carry the same subject/from/snippet
 * payload. Returns null for any other activity.
 */
function readFiledEmailPreview(
  activity: CollectionThreadActivityDto,
): FiledEmailPreview | null {
  if (activity.kind !== 'system_event') return null;
  const payload = activity.payload ?? {};
  if (
    payload.event !== 'evidence_filed_via_email' &&
    payload.event !== 'evidence_auto_matched' &&
    payload.event !== 'evidence_filed_via_upload'
  ) {
    return null;
  }

  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.length > 0 ? v : null;

  return {
    inboundEmailId: str(payload.inboundEmailId),
    subject: str(payload.subject),
    from: str(payload.from),
    snippet: str(payload.snippet),
    attachmentCount:
      typeof payload.attachmentCount === 'number' ? payload.attachmentCount : 0,
  };
}

interface SentEmailPreview {
  outboundEmailId: string | null;
  subject: string | null;
  to: string | null;
  snippet: string | null;
  attachmentCount: number;
}

/**
 * Reads the point-in-time email preview off an `email_sent` system event —
 * written when a rep sends an email from the thread's draft dialog. Returns
 * null for any other activity.
 */
function readSentEmailPreview(
  activity: CollectionThreadActivityDto,
): SentEmailPreview | null {
  if (activity.kind !== 'system_event') return null;
  const payload = activity.payload ?? {};
  if (payload.event !== 'email_sent') return null;

  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.length > 0 ? v : null;

  return {
    outboundEmailId: str(payload.outboundEmailId),
    subject: str(payload.subject),
    to: str(payload.to),
    snippet: str(payload.snippet),
    attachmentCount:
      typeof payload.attachmentCount === 'number' ? payload.attachmentCount : 0,
  };
}

/**
 * Shared preview card for email activities — filed inbound emails show a
 * "From …" address line, sent emails a "To …" line; both offer a click-through
 * to the full email in a side sheet when the email id survived serialization.
 */
function EmailActivityCard({
  subject,
  addressLine,
  snippet,
  attachmentCount,
  onView,
}: {
  subject: string | null;
  addressLine: React.ReactNode | null;
  snippet: string | null;
  attachmentCount: number;
  onView: (() => void) | null;
}) {
  return (
    <div className="mt-1.5 rounded-md border bg-muted/40 px-3 py-2">
      <div className="flex items-start gap-2">
        <EnvelopeSimpleIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground truncate">
            {subject ?? (
              <T context="placeholder, email without a subject">(no subject)</T>
            )}
          </div>
          {addressLine && (
            <div className="text-xs text-muted-foreground truncate">
              {addressLine}
            </div>
          )}
          {snippet && (
            <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
              {snippet}
            </div>
          )}
          <div className="flex items-center gap-3 mt-1.5">
            {attachmentCount > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <PaperclipIcon className="h-3.5 w-3.5" />
                {attachmentCount}
              </span>
            )}
            {onView && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={onView}
              >
                <T context="button, open the email in a side sheet">
                  View email
                </T>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface FiledDocumentPreview {
  documentId: string | null;
  fileName: string | null;
  classificationType: string | null;
  fileSizeBytes: number | null;
}

/**
 * Reads the point-in-time document preview off an `evidence_filed_via_document`
 * system event (manual document upload). Returns null for any other activity.
 */
function readFiledDocumentPreview(
  activity: CollectionThreadActivityDto,
): FiledDocumentPreview | null {
  if (activity.kind !== 'system_event') return null;
  const payload = activity.payload ?? {};
  if (payload.event !== 'evidence_filed_via_document') return null;

  const str = (v: unknown): string | null =>
    typeof v === 'string' && v.length > 0 ? v : null;

  return {
    documentId: str(payload.documentId),
    fileName: str(payload.fileName),
    classificationType: str(payload.classificationType),
    fileSizeBytes:
      typeof payload.fileSizeBytes === 'number' ? payload.fileSizeBytes : null,
  };
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function FiledDocumentCard({
  preview,
  onView,
}: {
  preview: FiledDocumentPreview;
  onView: (documentId: string) => void;
}) {
  const { documentId, fileName, classificationType, fileSizeBytes } = preview;
  return (
    <div className="mt-1.5 rounded-md border bg-muted/40 px-3 py-2">
      <div className="flex items-start gap-2">
        <FileTextIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground truncate">
            {fileName ?? (
              <T context="placeholder, document without a name">Document</T>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
            {classificationType && (
              <span className="capitalize">
                {classificationType.replace(/_/g, ' ')}
              </span>
            )}
            {fileSizeBytes != null && (
              <span>{formatFileSize(fileSizeBytes)}</span>
            )}
            {documentId && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => onView(documentId)}
              >
                <T context="button, open the filed document in a side sheet">
                  View document
                </T>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function toneFor(kind: CollectionThreadActivityDto['kind']): ActivityTone {
  switch (kind) {
    case 'status_change':
      return 'amber';
    case 'assignment_change':
      return 'purple';
    case 'system_event':
    case 'background_event':
      return 'stone';
    case 'comment':
    // next_step rows render via their own dedicated branch in ActivityRow;
    // these cases exist for exhaustiveness (and any future fall-through).
    case 'next_step':
      return 'note';
  }
}

function iconFor(kind: CollectionThreadActivityDto['kind']): React.ReactNode {
  switch (kind) {
    case 'comment':
      return <ChatTextIcon className="h-3.5 w-3.5" />;
    case 'next_step':
      return <NotePencilIcon className="h-3.5 w-3.5" />;
    case 'status_change':
      return <CheckCircleIcon className="h-3.5 w-3.5" />;
    case 'assignment_change':
      return <UserIcon className="h-3.5 w-3.5" />;
    case 'system_event':
      return <GearIcon className="h-3.5 w-3.5" />;
    case 'background_event':
      return <ArrowsClockwiseIcon className="h-3.5 w-3.5" />;
  }
}

function describe(activity: CollectionThreadActivityDto): string {
  const payload = activity.payload ?? {};

  if (activity.kind === 'status_change') {
    const to = typeof payload.to === 'string' ? payload.to : '';
    const from = typeof payload.from === 'string' ? payload.from : '';
    if (to === 'resolved') {
      const res =
        typeof payload.resolution === 'string' ? payload.resolution : '';
      const label =
        res && isValidResolution(res) ? RESOLUTION_LABELS[res] : res;
      return label ? `Resolved · ${label}` : 'Resolved';
    }
    if (to === 'open' && from === 'resolved') return 'Reopened';
    return `${from || '?'} → ${to || '?'}`;
  }

  if (activity.kind === 'assignment_change') {
    const from = payload.from;
    const to = payload.to;
    if (from == null && typeof to === 'string') return 'Assigned';
    if (typeof from === 'string' && to == null) return 'Unassigned';
    return 'Reassigned';
  }

  if (activity.kind === 'system_event') {
    const event = typeof payload.event === 'string' ? payload.event : 'event';
    if (event === 'evidence_auto_matched') {
      const from =
        typeof payload.from === 'string' && payload.from ? payload.from : null;
      const subject =
        typeof payload.subject === 'string' && payload.subject
          ? payload.subject
          : null;
      const who = from ?? 'Someone';
      return subject ? `${who} sent email: ${subject}` : `${who} sent an email`;
    }
    if (event === 'evidence_filed_via_email') return 'Filed email as evidence';
    if (event === 'evidence_filed_via_upload')
      return 'Filed uploaded email as evidence';
    if (event === 'evidence_historical_emails_linked') {
      // One entry stands for the whole batch the invoice-identifier search
      // attached, so the count carries the information a per-email entry would.
      const count =
        typeof payload.emailCount === 'number' ? payload.emailCount : 0;
      const invoiceNumber =
        typeof payload.invoiceNumber === 'string' && payload.invoiceNumber
          ? payload.invoiceNumber
          : null;
      const emails =
        count === 1 ? '1 earlier email' : `${count} earlier emails`;
      return invoiceNumber
        ? `Attached ${emails} mentioning invoice ${invoiceNumber}`
        : `Attached ${emails}`;
    }
    if (event === 'email_sent') return 'Sent email';
    if (event === 'evidence_filed_via_document')
      return 'Filed document as evidence';
    if (event === 'evidence_filed_via_portal')
      return 'Filed document fetched from the portal';
    if (event === 'portal_action_auto_dispatched') {
      // An unattended portal sign-in nobody approved — the timeline is the only
      // place it surfaces, so name the action and carry the model's reason.
      const label =
        typeof payload.label === 'string' && payload.label
          ? payload.label
          : 'portal action';
      const rationale =
        typeof payload.rationale === 'string' && payload.rationale
          ? payload.rationale
          : null;
      const base = `Agent ran "${label}" on the portal without approval`;
      return rationale ? `${base} — ${rationale}` : base;
    }
    if (event === 'portal_fetch_task_enqueued') {
      // The queued counterpart of portal_action_auto_dispatched: the fetch
      // was handed to a background agent's task queue rather than run now, so
      // name the action, the task, and the model's reason.
      const label =
        typeof payload.label === 'string' && payload.label
          ? payload.label
          : 'portal action';
      const taskDisplayId =
        typeof payload.taskDisplayId === 'string' && payload.taskDisplayId
          ? payload.taskDisplayId
          : null;
      const rationale =
        typeof payload.rationale === 'string' && payload.rationale
          ? payload.rationale
          : null;
      const base = taskDisplayId
        ? `Queued "${label}" for the portal agent (task ${taskDisplayId})`
        : `Queued "${label}" for the portal agent`;
      return rationale ? `${base} — ${rationale}` : base;
    }
    if (event === 'remediation_interpreted') {
      // Emitted when the discrepancy interpreter retro-fits a likely cause
      // onto an existing thread (backfill path) — records why the title changed.
      const label = typeof payload.label === 'string' ? payload.label : null;
      const explanation =
        typeof payload.explanation === 'string' && payload.explanation
          ? payload.explanation
          : null;
      const base = label
        ? `Likely cause identified: ${label}`
        : 'Likely cause identified';
      return explanation ? `${base} — ${explanation}` : base;
    }
    if (event === 'thread_created') {
      const reason =
        typeof payload.reason === 'string' && payload.reason.length > 0
          ? payload.reason
          : null;
      let label = reason ? `Thread opened — ${reason}` : 'Thread opened';
      if (payload.initialAmount != null) {
        label += ` · Initial amount ${formatCurrency(String(payload.initialAmount))}`;
        if (payload.initialAmountSource === 'remittance_ingestion') {
          label += ' (estimated from remittance)';
        }
      }
      return label;
    }
    return event.replace(/_/g, ' ');
  }

  if (activity.kind === 'background_event') {
    const event = typeof payload.event === 'string' ? payload.event : 'event';
    if (event === 'invoice_amounts_updated') {
      // Ingest-driven title/amount rewrite after the linked invoice's money
      // fields changed in Famous (e.g. a payment applied). Deliberately does
      // not count toward thread staleness — see the backend's
      // collectionThreadTitleRefresh module.
      // Neutral fallback for unknown thread types so a future refreshable
      // type never renders mislabeled as a short pay ("Thread amount updated…").
      const kindLabel =
        payload.threadType === 'over_payment'
          ? 'Over-payment'
          : payload.threadType === 'short_pay'
            ? 'Short-pay'
            : 'Thread';
      const amount =
        typeof payload.amount === 'string'
          ? formatCurrency(payload.amount)
          : null;
      const previousAmount =
        typeof payload.previousAmount === 'string'
          ? formatCurrency(payload.previousAmount)
          : null;
      if (amount && previousAmount && previousAmount !== amount) {
        return `${kindLabel} amount updated from Famous: ${previousAmount} → ${amount}`;
      }
      return amount
        ? `${kindLabel} amount updated from Famous: ${amount}`
        : `${kindLabel} amount updated from Famous`;
    }
    return event.replace(/_/g, ' ');
  }

  return '';
}
